/**
 * Glue hook that owns the KotH round lifecycle inside the main lobby:
 *
 *   - Resolves the active round id from `?round=<id>` URL param, falling back to
 *     a cached id in localStorage so reloading the lobby snaps back to the
 *     same round.
 *   - Polls the `KothRound` PDA via `useKothRound`.
 *   - Exposes write actions (`startRound`, `touch`, `endRound`) bound to the
 *     connected wallet.
 *
 * The returned object is shared by `<KothThroneStation>` (rendered inside the
 * R3F canvas) and `<KothHud>` (DOM overlay), so both surfaces stay coherent.
 */
import { BN } from "@coral-xyz/anchor";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { PublicKey, SystemProgram } from "@solana/web3.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  buildWalletProgram,
  deriveRoundPda,
  deriveSeasonPda,
  deriveLastTouchPda,
  deriveLeaderboardEntryPda,
} from "./anchor";
import { buildReadonlyProgram } from "../../explorer/anchor";
import { useKothRound, type KothRoundState } from "./useKothRound";
import { useCurrentOpenRound } from "./useCurrentOpenRound";
import { useCurrentRoundApi } from "./useCurrentRoundApi";
import {
  buildSessionKeyProgram,
  delegateSessionKey,
  getActiveSessionKey,
  revokeSessionKey,
  subscribeSessionKeyChange,
} from "../../session/sessionKey";
import { useChainNow } from "../../util/useChainNow";
import { getLaunch, seasonIdBytes } from "@solblox/shared";

const SEASON_LS_KEY = "solblox.season_id_hex";

function loadSeasonIdFromLocalStorage(): Uint8Array | null {
  if (typeof localStorage === "undefined") return null;
  const hex = localStorage.getItem(SEASON_LS_KEY);
  if (!hex || hex.length !== 64) return null;
  const out = new Uint8Array(32);
  for (let i = 0; i < 32; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

/**
 * Resolve the active season id (32 bytes) for a launch. localStorage wins (the
 * dev console / a manual init-season writes it there for testing), else fall
 * back to the launch registry's `seasonIdHex` — which is how a fresh visitor on
 * crownclash.xyz (no localStorage, no dev console) reaches the wired mainnet
 * season. Without the registry fallback the lobby reports "no season configured".
 */
function resolveSeasonId(launchSlug: string): Uint8Array | null {
  const ls = loadSeasonIdFromLocalStorage();
  if (ls) return ls;
  const hex = getLaunch(launchSlug)?.seasonIdHex;
  if (hex && hex.length === 64) {
    try {
      return seasonIdBytes(hex);
    } catch {
      return null;
    }
  }
  return null;
}

interface MethodsBuilder {
  accounts(accounts: Record<string, unknown>): MethodsBuilder;
  rpc(): Promise<string>;
}
interface KothMethods {
  kothInitRound(roundId: BN, durationSecs: BN): MethodsBuilder;
  kothTouch(): MethodsBuilder;
  kothEndRound(): MethodsBuilder;
  kothTouchDelegated(): MethodsBuilder;
}

const DEFAULT_DURATION_SECS = 600;
export const KOTH_ROUND_LS_KEY = "koth.activeRoundId";
const LS_KEY = KOTH_ROUND_LS_KEY;

export interface KothLobbyController {
  roundId: BN | null;
  round: KothRoundState | null;
  pda: PublicKey | null;
  expired: boolean;
  settled: boolean;
  isYouKing: boolean;
  localWalletPubkey: PublicKey | null;
  walletConnected: boolean;
  txInFlight: boolean;
  txError: string | null;
  lastSig: string | null;
  pollError: string | null;
  startRound: () => Promise<void>;
  touch: () => Promise<void>;
  endRound: () => Promise<void>;
  clearStoredRound: () => void;
  // Season closure metadata for the stale-claim countdown. Both fields are
  // null until the season is closed AND the IDL ships `closed_at` /
  // `stale_claim_window_secs`; UI must sentinel-guard before rendering.
  seasonClosedAt: number | null;
  staleClaimWindowSecs: number | null;
  // Session-key affordances — undefined sessionKeyExpiresAt means no active key.
  sessionKeyExpiresAt: number | null;
  enableSessionKey: () => Promise<void>;
  disableSessionKey: () => Promise<void>;
}

export interface UseKothLobbyControllerOpts {
  /**
   * /watch broadcast mode: auto-discover and follow the current OPEN round
   * on-chain (switching as the keeper rolls rounds) instead of the
   * `?round=`/localStorage id. Off for the normal player lobby. See
   * {@link useCurrentOpenRound}.
   */
  followOpenRound?: boolean;
}

export function useKothLobbyController(
  opts: UseKothLobbyControllerOpts = {},
): KothLobbyController {
  const { connection } = useConnection();
  const wallet = useWallet();
  const [searchParams, setSearchParams] = useSearchParams();

  // Which launch this client is for (?launch=, default crownclash) — drives the
  // registry season lookup so a fresh visitor resolves the wired season.
  const launchSlug = searchParams.get("launch") ?? "crownclash";

  // Season scope for open-round discovery (the program is shared across
  // launches). Resolves from localStorage then the launch registry.
  const followSeasonPda = useMemo<PublicKey | null>(() => {
    const sid = resolveSeasonId(launchSlug);
    if (!sid) return null;
    try {
      return deriveSeasonPda(sid);
    } catch {
      return null;
    }
  }, [launchSlug]);
  const followId = useCurrentOpenRound(
    opts.followOpenRound === true,
    followSeasonPda,
  );
  // Normal-lobby open-round discovery via the API (Option B) — follows the
  // keeper's rolling round ids without each browser scanning the chain. Off in
  // broadcast mode (that uses on-chain followId above).
  const apiRoundId = useCurrentRoundApi(opts.followOpenRound !== true);

  // Resolve roundId, in precedence:
  //   broadcast on-chain follow > explicit ?round= > API-discovered live round >
  //   localStorage > null.
  // Explicit ?round= still wins over discovery so a shared/test link pins a round.
  const roundIdParam = searchParams.get("round");
  const roundId = useMemo<BN | null>(() => {
    if (followId) return followId;
    if (roundIdParam) {
      try {
        return new BN(roundIdParam, 10);
      } catch {
        return null;
      }
    }
    if (apiRoundId) return apiRoundId;
    const ls =
      typeof localStorage !== "undefined" ? localStorage.getItem(LS_KEY) : null;
    if (!ls) return null;
    try {
      return new BN(ls, 10);
    } catch {
      return null;
    }
  }, [roundIdParam, followId, apiRoundId]);

  const { round, pda, error: pollError, refresh } = useKothRound(roundId);

  // Tick at 1Hz so `expired` flips without a fresh poll. MUST use chain time —
  // surfpool's Clock can sit ~109 min ahead of host wall-clock, so comparing
  // `expires_at` to `Date.now()` lies and lets the touch button stay live after
  // the round is on-chain-expired (KothRoundNotOpen #6010).
  const nowSec = useChainNow(1000);

  const [txInFlight, setTxInFlight] = useState(false);
  const [txError, setTxError] = useState<string | null>(null);
  const [lastSig, setLastSig] = useState<string | null>(null);

  const localWalletPubkey = wallet.publicKey ?? null;
  const settled = round?.status === 1;
  const expiresAt = round ? Number(round.expiresAt.toString()) : 0;
  const expired = !!round && !settled && nowSec >= expiresAt;
  const isYouKing =
    !!round && !!localWalletPubkey && round.currentKing.equals(localWalletPubkey);

  const startRound = useCallback(async (): Promise<void> => {
    if (!wallet.connected || !wallet.publicKey) return;
    setTxInFlight(true);
    setTxError(null);
    try {
      const seasonId = resolveSeasonId(launchSlug);
      if (!seasonId) {
        throw new Error(
          "no season configured for this launch — set its seasonIdHex in shared/launches.ts (or run init-season in the ChainTab debug panel)",
        );
      }
      const newRoundId = new BN(Date.now());
      const program = buildWalletProgram(connection, wallet);
      const methods = program.methods as unknown as KothMethods;
      const newPda = deriveRoundPda(newRoundId, program.programId);
      const seasonPda = deriveSeasonPda(seasonId, program.programId);
      const sig = await methods
        .kothInitRound(newRoundId, new BN(DEFAULT_DURATION_SECS))
        .accounts({
          round: newPda,
          season: seasonPda,
          admin: wallet.publicKey,
          payer: wallet.publicKey,
          systemProgram: SystemProgram.programId,
        })
        .rpc();
      setLastSig(sig);
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(LS_KEY, newRoundId.toString());
      }
      setSearchParams({ round: newRoundId.toString() }, { replace: true });
    } catch (e) {
      setTxError(extractMessage(e));
    } finally {
      setTxInFlight(false);
    }
  }, [connection, wallet, setSearchParams, launchSlug]);

  // Bump when delegate/revoke happens — or when the IDB-backed store finishes
  // hydrating — so memo'd active-key reads re-run.
  const [sessionKeyVersion, setSessionKeyVersion] = useState(0);
  useEffect(() => {
    return subscribeSessionKeyChange(() => {
      setSessionKeyVersion((v) => v + 1);
    });
  }, []);
  const activeSessionKey = useMemo(() => {
    if (!wallet.publicKey) return null;
    return getActiveSessionKey(wallet.publicKey);
    // sessionKeyVersion is the signal — public key + version are enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet.publicKey, sessionKeyVersion]);

  const touch = useCallback(async (): Promise<void> => {
    if (!wallet.connected || !wallet.publicKey || !roundId || !pda) return;
    setTxInFlight(true);
    setTxError(null);
    try {
      const owner = wallet.publicKey;
      const active = activeSessionKey;
      if (active) {
        const program = buildSessionKeyProgram(connection, active.keypair);
        const methods = program.methods as unknown as KothMethods;
        const lastTouch = deriveLastTouchPda(pda, owner);
        const sig = await methods
          .kothTouchDelegated()
          .accounts({
            round: pda,
            lastTouch,
            toucher: owner,
            sessionKeySigner: active.keypair.publicKey,
            sessionKey: active.sessionKeyPda,
            owner,
            systemProgram: SystemProgram.programId,
          })
          .rpc();
        setLastSig(sig);
        refresh();
        return;
      }
      const program = buildWalletProgram(connection, wallet);
      const methods = program.methods as unknown as KothMethods;
      const lastTouch = deriveLastTouchPda(pda, owner);
      const sig = await methods
        .kothTouch()
        .accounts({
          round: pda,
          lastTouch,
          toucher: owner,
          systemProgram: SystemProgram.programId,
        })
        .rpc();
      setLastSig(sig);
      refresh();
    } catch (e) {
      setTxError(extractMessage(e));
    } finally {
      setTxInFlight(false);
    }
  }, [connection, wallet, roundId, pda, refresh, activeSessionKey]);

  const enableSessionKey = useCallback(async (): Promise<void> => {
    if (!wallet.connected || !wallet.publicKey) return;
    setTxInFlight(true);
    setTxError(null);
    try {
      await delegateSessionKey(connection, wallet);
      setSessionKeyVersion((v) => v + 1);
    } catch (e) {
      setTxError(extractMessage(e));
    } finally {
      setTxInFlight(false);
    }
  }, [connection, wallet]);

  const disableSessionKey = useCallback(async (): Promise<void> => {
    if (!wallet.connected || !wallet.publicKey) return;
    setTxInFlight(true);
    setTxError(null);
    try {
      const sig = await revokeSessionKey(connection, wallet);
      if (sig) setLastSig(sig);
      setSessionKeyVersion((v) => v + 1);
    } catch (e) {
      setTxError(extractMessage(e));
    } finally {
      setTxInFlight(false);
    }
  }, [connection, wallet]);

  const endRound = useCallback(async (): Promise<void> => {
    if (!wallet.connected || !wallet.publicKey || !roundId || !pda || !round) {
      return;
    }
    setTxInFlight(true);
    setTxError(null);
    try {
      // Prefer the season PDA recorded on the round itself — localStorage can
      // drift if the player ran init-season multiple times since this round
      // was created, which triggers EndRoundSeasonMismatch (#6023).
      let seasonPda: PublicKey | null = round.season ?? null;
      if (!seasonPda) {
        const seasonBytes = resolveSeasonId(launchSlug);
        if (!seasonBytes) {
          throw new Error(
            "round has no on-chain season and no cached season id — init a season first",
          );
        }
        seasonPda = deriveSeasonPda(seasonBytes);
      }
      if (!round.recentTouchers) {
        throw new Error(
          "this round was inited before recentTouchers existed — init fresh round and retry",
        );
      }
      const populated = round.recentTouchers.filter(
        (p) => !p.equals(PublicKey.default),
      );
      const remaining = populated.map((wallet) => ({
        pubkey: deriveLeaderboardEntryPda(seasonPda, wallet),
        isSigner: false,
        isWritable: true,
      }));
      const program = buildWalletProgram(connection, wallet);
      const methods = program.methods as unknown as KothMethods;
      const sig = await (
        methods
          .kothEndRound()
          .accounts({
            round: pda,
            season: seasonPda,
            keeper: wallet.publicKey,
            systemProgram: SystemProgram.programId,
          }) as unknown as {
          remainingAccounts: (a: typeof remaining) => MethodsBuilder;
        }
      )
        .remainingAccounts(remaining)
        .rpc();
      setLastSig(sig);
      refresh();
    } catch (e) {
      setTxError(extractMessage(e));
    } finally {
      setTxInFlight(false);
    }
  }, [connection, wallet, roundId, pda, round, refresh, launchSlug]);

  const clearStoredRound = useCallback((): void => {
    if (typeof localStorage !== "undefined") localStorage.removeItem(LS_KEY);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const seasonPda = useMemo<PublicKey | null>(() => {
    if (round?.season) return round.season;
    const cached = resolveSeasonId(launchSlug);
    if (!cached) return null;
    try {
      return deriveSeasonPda(cached);
    } catch {
      return null;
    }
  }, [round, launchSlug]);

  const [seasonClosedAt, setSeasonClosedAt] = useState<number | null>(null);
  const [staleClaimWindowSecs, setStaleClaimWindowSecs] = useState<number | null>(
    null,
  );
  useEffect(() => {
    if (!seasonPda) {
      setSeasonClosedAt(null);
      setStaleClaimWindowSecs(null);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const program = buildReadonlyProgram(connection);
    const ns = (
      program.account as unknown as Record<
        string,
        { fetch: (pk: PublicKey) => Promise<unknown> } | undefined
      >
    )["season"];
    if (!ns || typeof ns.fetch !== "function") return;

    const tick = async (): Promise<void> => {
      try {
        const acct = (await ns.fetch(seasonPda)) as {
          closedAt?: BN | number;
          staleClaimWindowSecs?: BN | number;
        };
        if (cancelled) return;
        const closedRaw = acct.closedAt;
        const windowRaw = acct.staleClaimWindowSecs;
        const closed =
          closedRaw === undefined
            ? null
            : typeof closedRaw === "number"
              ? closedRaw
              : Number(closedRaw.toString());
        const win =
          windowRaw === undefined
            ? null
            : typeof windowRaw === "number"
              ? windowRaw
              : Number(windowRaw.toString());
        setSeasonClosedAt(closed && closed > 0 ? closed : null);
        setStaleClaimWindowSecs(win && win > 0 ? win : null);
      } catch {
        if (!cancelled) {
          setSeasonClosedAt(null);
          setStaleClaimWindowSecs(null);
        }
      }
      if (!cancelled) timer = setTimeout(tick, 60_000);
    };
    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [connection, seasonPda]);

  return {
    roundId,
    round,
    pda,
    expired,
    settled: !!settled,
    isYouKing,
    localWalletPubkey,
    walletConnected: !!wallet.connected,
    txInFlight,
    txError,
    lastSig,
    pollError,
    startRound,
    touch,
    endRound,
    clearStoredRound,
    seasonClosedAt,
    staleClaimWindowSecs,
    sessionKeyExpiresAt: activeSessionKey?.expiresAt ?? null,
    enableSessionKey,
    disableSessionKey,
  };
}

function extractMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e);
}
